import { useEffect, useRef, useState } from "react";
import PetInformation from "../PetInformation";

const TECHNICAL_FIELDS: { key: string; label: string }[] = [
  { key: "position", label: "Position:" },
  { key: "paperSpeed", label: "ECG Paper Speed:" },
  { key: "sensitivity", label: "Sensitivity:" },
  { key: "leads", label: "Leads Recorded:" },
  { key: "quality", label: "Recording Quality:" },
];

const FINDING_FIELDS: { key: string; label: string }[] = [
  { key: "rhythm", label: "Cardiac Rhythm:" },
  { key: "pAmplitude", label: "P Wave Amplitude (mV):" },
  { key: "pDuration", label: "P Wave Duration (sec):" },
  { key: "prInterval", label: "PR Interval (sec):" },
  { key: "qrsDuration", label: "QRS Duration (sec):" },
  { key: "rAmplitude", label: "R Wave Amplitude (mV):" },
  { key: "qrsMorphology", label: "QRS Morphology:" },
  { key: "tMorphology", label: "T Wave Morphology:" },
  { key: "axis", label: "Electrical Axis:" },
  { key: "arrhythmias", label: "Arrhythmias Identified:" },
  { key: "additional", label: "Additional Findings:" },
];

const LABEL_WIDTH = "w-52";

const ECGReport = () => {
  const [indication, setIndication] = useState("");
  const [technical, setTechnical] = useState<Record<string, string>>({
    position: "",
    paperSpeed: "",
    sensitivity: "",
    leads: "",
    quality: "",
  });
  const [heartRate, setHeartRate] = useState("");
  const [findings, setFindings] = useState<Record<string, string>>({
    rhythm: "",
    pAmplitude: "",
    pDuration: "",
    prInterval: "",
    qrsDuration: "",
    rAmplitude: "",
    qrsMorphology: "",
    tMorphology: "",
    axis: "",
    arrhythmias: "",
    additional: "",
  });
  const [interpretation, setInterpretation] = useState("");
  const [recommendations, setRecommendations] = useState("");

  const interpretationRef = useRef<HTMLTextAreaElement | null>(null);
  const recommendationsRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = (ref: React.RefObject<HTMLTextAreaElement>) => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight(interpretationRef);
  }, [interpretation]);

  useEffect(() => {
    adjustHeight(recommendationsRef);
  }, [recommendations]);

  const inputClass = "border-b border-gray-500 text-xs p-0 focus:outline-none flex-1";

  return (
    <div className="overflow-x-auto p-4">
      <PetInformation />

      <div className="flex items-center gap-2 mt-3">
        <p className={`text-xs font-semibold ${LABEL_WIDTH} shrink-0`}>Indication for ECG:</p>
        <input
          type="text"
          value={indication}
          onChange={(e) => setIndication(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold border-b border-gray-500 inline-block">TECHNICAL DETAILS</p>
        {TECHNICAL_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2 mt-2">
            <p className={`text-xs font-semibold ${LABEL_WIDTH} shrink-0`}>{label}</p>
            <input
              type="text"
              value={technical[key]}
              onChange={(e) => setTechnical((prev) => ({ ...prev, [key]: e.target.value }))}
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold border-b border-gray-500 inline-block">ECG FINDINGS</p>
        <div className="flex items-center gap-2 mt-2">
          <p className={`text-xs font-semibold ${LABEL_WIDTH} shrink-0`}>Heart Rate:</p>
          <input
            type="text"
            value={heartRate}
            onChange={(e) => setHeartRate(e.target.value)}
            className="border-b border-gray-500 text-xs p-0 focus:outline-none w-24"
          />
          <span className="text-xs">bpm</span>
        </div>
        {FINDING_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2 mt-2">
            <p className={`text-xs font-semibold ${LABEL_WIDTH} shrink-0`}>{label}</p>
            <input
              type="text"
              value={findings[key]}
              onChange={(e) => setFindings((prev) => ({ ...prev, [key]: e.target.value }))}
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold border-b border-gray-500 inline-block">INTERPRETATION</p>
        <textarea
          ref={interpretationRef}
          value={interpretation}
          onChange={(e) => setInterpretation(e.target.value)}
          onInput={() => adjustHeight(interpretationRef)}
          className="w-full border p-2 text-xs resize-none mt-1 print:hidden"
          style={{ overflowY: "auto", scrollbarWidth: "thin", minHeight: "80px" }}
        />
        <div className="hidden print:block w-full p-2 text-xs whitespace-pre-wrap border-b border-gray-500 min-h-[80px]">
          {interpretation}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold border-b border-gray-500 inline-block">RECOMMENDATIONS</p>
        <textarea
          ref={recommendationsRef}
          value={recommendations}
          onChange={(e) => setRecommendations(e.target.value)}
          onInput={() => adjustHeight(recommendationsRef)}
          className="w-full border p-2 text-xs resize-none mt-1 print:hidden"
          style={{ overflowY: "auto", scrollbarWidth: "thin", minHeight: "120px" }}
        />
        <div className="hidden print:block w-full p-2 text-xs whitespace-pre-wrap border-b border-gray-500 min-h-[120px]">
          {recommendations}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <p className="text-xs">Doctor's Name:</p>
        <input type="text" className="text-sm w-[400px] border p-1" />
      </div>

      <p className="mt-3 italic text-xs">This report is not valid for Vetero-Legal Purposes</p>
    </div>
  );
};

export default ECGReport;
